import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  deriveThemeColor,
  isHexColor,
  ThemeColorValue
} from '@/domain/branding'

interface ThemeColorFieldProps {
  label: string
  value: ThemeColorValue | null
  disabled: boolean
  onChange: (value: ThemeColorValue | null) => void
}

const SWATCH_FALLBACK = '#ffffff'
const SIX_DIGIT_HEX = /^#[0-9a-fA-F]{6}$/

export function ThemeColorField({
  label,
  value,
  disabled,
  onChange
}: ThemeColorFieldProps) {
  const { t } = useTranslation()
  const [showVariants, setShowVariants] = useState(false)

  return (
    <div className="flex flex-col gap-2">
      {/* Next to the label rather than pushed to the far edge: justified apart, the reset landed
          in the gap between grid columns and read as belonging to neither field. */}
      <div className="flex items-baseline gap-3">
        <label className="text-sm text-gray-500">{label}</label>
        {value && !disabled && (
          <button
            type="button"
            className="text-xs text-gray-400 underline"
            onClick={() => onChange(null)}
          >
            {t('custom_theme.use_default')}
          </button>
        )}
      </div>

      <HexInput
        label={label}
        value={value?.main ?? ''}
        disabled={disabled}
        withSwatch
        placeholder={t('custom_theme.not_configured')}
        // Picking a main colour re-derives the variants; a merchant who wants different ones
        // sets them below, and those edits are not overwritten until main changes again.
        onCommit={(main) => onChange(deriveThemeColor(main))}
      />

      {value && (
        <>
          <button
            type="button"
            className="self-start text-xs text-gray-500 underline"
            onClick={() => setShowVariants(!showVariants)}
            aria-expanded={showVariants}
          >
            {showVariants
              ? t('custom_theme.hide_variants')
              : t('custom_theme.show_variants')}
          </button>

          {showVariants && (
            <div className="flex flex-col gap-2 border-l-2 border-gray-200 pl-3">
              {(['light', 'dark', 'contrastText'] as const).map((variant) => (
                <div key={variant} className="flex flex-col gap-1">
                  <span className="text-xs text-gray-400">
                    {t(`custom_theme.variant_${variant}`)}
                  </span>
                  <HexInput
                    label={`${label} — ${t(`custom_theme.variant_${variant}`)}`}
                    value={value[variant]}
                    disabled={disabled}
                    withSwatch
                    onCommit={(hex) => onChange({ ...value, [variant]: hex })}
                  />
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  )
}

interface ColorFieldProps {
  label: string
  hint: string
  value: string
  disabled: boolean
  onChange: (value: string) => void
}

// A single colour with no variants — the text drawn over the background image. A brand colour
// goes through ThemeColorField instead, because the portal only applies one with all four shades.
export function ColorField({
  label,
  hint,
  value,
  disabled,
  onChange
}: ColorFieldProps) {
  return (
    <div className="flex flex-col gap-2">
      <label className="text-sm text-gray-500">{label}</label>
      <HexInput
        label={label}
        value={value}
        disabled={disabled}
        withSwatch
        placeholder="#000000"
        onCommit={onChange}
      />
      <span className="text-xs text-gray-400">{hint}</span>
    </div>
  )
}

interface HexInputProps {
  // Accessible name for both controls. Neither has a visible <label> bound to it, and the
  // placeholder describes the empty state rather than the field, so it cannot serve as the name.
  label: string
  value: string
  disabled: boolean
  withSwatch?: boolean
  placeholder?: string
  onCommit: (hex: string) => void
}

// Holds what is being typed locally and only commits a value that parses, so the form never
// carries a half-typed colour — the endpoint refuses a partial one and the portal ignores it.
function HexInput({
  label,
  value,
  disabled,
  withSwatch,
  placeholder,
  onCommit
}: HexInputProps) {
  const [text, setText] = useState(value)

  useEffect(() => {
    setText(value)
  }, [value])

  // Empty commits as well as a valid hex: clearing the field is how an attribute is unset, which
  // for a brand colour means the portal goes back to its default palette.
  const isCommittable = (value: string) => value === '' || isHexColor(value)

  const commit = (next: string) => {
    setText(next)
    if (isCommittable(next)) onCommit(next)
  }

  return (
    <div className="flex items-center gap-2">
      {withSwatch && (
        <input
          type="color"
          aria-label={label}
          // Explicit height on both controls: stretching left the swatch a couple of pixels off
          // the field, because the two have different intrinsic heights.
          // The browser draws the colour inside a padded, bordered wrapper; both are cleared so
          // the colour reaches the edges of the rounded box.
          className="size-10 shrink-0 cursor-pointer rounded-md border border-gray-300 p-0 disabled:cursor-not-allowed [&::-moz-color-swatch]:rounded-[5px] [&::-moz-color-swatch]:border-none [&::-webkit-color-swatch-wrapper]:p-0 [&::-webkit-color-swatch]:rounded-[5px] [&::-webkit-color-swatch]:border-none"
          value={SIX_DIGIT_HEX.test(text) ? text : SWATCH_FALLBACK}
          disabled={disabled}
          onChange={(e) => commit(e.target.value)}
        />
      )}
      <input
        type="text"
        aria-label={label}
        className="h-10 w-28 rounded-md border border-gray-300 px-2 font-mono text-sm disabled:bg-gray-100"
        value={text}
        placeholder={placeholder}
        disabled={disabled}
        onChange={(e) => commit(e.target.value)}
        // A value that never parsed is not a change; showing the stored one back avoids
        // leaving an unsaved-looking field that would save nothing.
        onBlur={() => !isCommittable(text) && setText(value)}
      />
    </div>
  )
}

export default ThemeColorField
